import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Sparkles, Terminal, Ban, RotateCw } from "lucide-react";
import { SpawnsApi } from "../lib/api";
import { qk } from "../lib/query";
import { useStreamEvent } from "../lib/sse";
import { useToast } from "../lib/toast";
import { ListDetail } from "../components/ListDetail";
import { Button, Pill, EmptyState, CenteredSpinner } from "../components/primitives";
import { PageHeader } from "../components/PageScaffold";
import { relativeTime, formatDuration, formatBytes } from "../lib/format";
import { cn } from "../lib/cn";
import { getHashParam } from "./registry";
import type { SpawnMeta } from "../lib/types";

const STATUSES = ["running", "completed", "failed", "killed"] as const;

export default function Spawns() {
  const qc = useQueryClient();
  const [persona, setPersona] = useState<string>(() => getHashParam("persona") ?? "");
  const [status, setStatus] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: spawns = [], isLoading, error } = useQuery({
    queryKey: qk.spawns.list({ persona, status }),
    queryFn: () => SpawnsApi.list({ persona: persona || undefined, status: status || undefined, limit: 500 }),
    refetchInterval: 10_000,
  });

  // Personas: derive from data
  const personas = Array.from(new Set(spawns.map((s) => s.persona))).sort();

  // Live: refresh on new spawn
  useStreamEvent("spawn", () => {
    qc.invalidateQueries({ queryKey: ["spawns"] });
  });

  const runningCount = spawns.filter((s) => s.status === "running").length;
  const failedCount = spawns.filter((s) => s.status === "failed" || s.status === "killed").length;

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Spawns"
        icon={Sparkles}
        subtitle={
          spawns.length
            ? `${spawns.length} recent · ${runningCount} running · ${failedCount} failed`
            : "every ephemeral persona run"
        }
      />
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        <ListDetail<SpawnMeta>
          items={spawns}
          itemKey={(s) => s.id}
          selectedId={selectedId}
          onSelect={setSelectedId}
          loading={isLoading}
          error={error as Error | null}
          itemHeight={72}
          toolbar={
            <div className="flex items-center gap-2 px-3 py-2">
              <select value={persona} onChange={(e) => setPersona(e.target.value)} className="h-7 text-xs w-32">
                <option value="">All personas</option>
                {personas.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-7 text-xs w-32">
                <option value="">All statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <span className="ml-auto text-[11px] text-fg-subtle font-mono">{spawns.length}</span>
            </div>
          }
          emptyState={<EmptyState icon={<Sparkles className="size-8" />} title="No spawns yet" />}
          renderItem={({ item }) => <SpawnRow s={item} />}
          renderDetail={(s) => <SpawnDetail id={s.id} onKilled={() => qc.invalidateQueries({ queryKey: ["spawns"] })} />}
        />
      </div>
    </div>
  );
}

function statusTone(s: string): "neutral" | "accent" | "success" | "warning" | "danger" {
  switch (s) {
    case "running":
      return "accent";
    case "completed":
      return "success";
    case "failed":
      return "danger";
    case "killed":
      return "warning";
    default:
      return "neutral";
  }
}

function SpawnRow({ s }: { s: SpawnMeta }) {
  const isRunning = s.status === "running";
  const dur = s.ended_at
    ? formatDuration(new Date(s.ended_at).getTime() - new Date(s.started_at).getTime())
    : isRunning
      ? formatDuration(Date.now() - new Date(s.started_at).getTime()) + "…"
      : "—";
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-2 mb-1">
        <span
          className={cn(
            "size-1.5 rounded-full flex-shrink-0",
            s.status === "running" && "bg-accent animate-pulse-dot",
            s.status === "completed" && "bg-success",
            s.status === "failed" && "bg-danger",
            s.status === "killed" && "bg-warning",
          )}
        />
        <span className="text-sm font-medium text-fg truncate">{s.persona}</span>
        <Pill tone={statusTone(s.status)}>{s.status}</Pill>
        <span className="ml-auto text-[11px] text-fg-subtle font-mono whitespace-nowrap">
          {relativeTime(s.started_at).replace(" ago", "")}
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs text-fg-muted">
        <span className="font-mono truncate max-w-[18rem]">{s.command}</span>
        <span className="ml-auto text-fg-subtle">{dur}</span>
        {typeof s.exit_code === "number" && (
          <span className={cn("font-mono", s.exit_code === 0 ? "text-success" : "text-danger")}>
            exit {s.exit_code}
          </span>
        )}
      </div>
    </div>
  );
}

function SpawnDetail({ id, onKilled }: { id: string; onKilled: () => void }) {
  const toast = useToast();
  const { data, isLoading, error } = useQuery({
    queryKey: qk.spawns.detail(id),
    queryFn: () => SpawnsApi.detail(id),
    // Poll while a spawn is open so a kill (or natural exit) reflects fast.
    refetchInterval: (q) => (q.state.data?.meta.status === "running" ? 3000 : false),
  });
  const retry = useMutation({
    mutationFn: () => SpawnsApi.retry(id),
    onSuccess: (r) => {
      // A busy persona queues the retry rather than losing it — say which happened.
      toast.success(r.queued ? "Retry queued — persona is busy, it will run when free" : "Retry started");
      onKilled();
    },
    onError: (e) => toast.error("Retry failed", String(e)),
  });
  const kill = useMutation({
    mutationFn: () => SpawnsApi.kill(id),
    onSuccess: (res) => {
      toast.success(res.already_dead ? "Spawn already exited" : "Kill signal sent");
      onKilled();
    },
    onError: (err) => toast.error("Kill failed", String(err)),
  });
  if (isLoading) return <CenteredSpinner />;
  if (error) return <div className="p-5"><Pill tone="danger">Failed to load: {String(error)}</Pill></div>;
  if (!data) return null;
  const { meta, trigger, prompt, stdout, stderr } = data;
  const isRunning = meta.status === "running";
  const dur = meta.ended_at
    ? formatDuration(new Date(meta.ended_at).getTime() - new Date(meta.started_at).getTime())
    : "running…";
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-3 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-fg">{meta.persona}</h2>
              <Pill tone={statusTone(meta.status)}>{meta.status}</Pill>
              {typeof meta.exit_code === "number" && (
                <Pill tone={meta.exit_code === 0 ? "success" : "danger"}>exit {meta.exit_code}</Pill>
              )}
              {meta.signal && <Pill tone="warning">signal {meta.signal}</Pill>}
            </div>
            <button
              type="button"
              onClick={() => { void navigator.clipboard?.writeText(meta.id); toast.success("Spawn id copied"); }}
              title="Copy the spawn id — this is what to quote when referring to a run"
              className="text-[11px] text-fg-subtle font-mono mt-1 hover:text-fg [overflow-wrap:anywhere] text-left"
            >
              {meta.id}
            </button>
            <div className="text-xs text-fg-muted font-mono mt-1 [overflow-wrap:anywhere]">{meta.command}</div>
            <div className="text-[11px] text-fg-subtle mt-1.5 flex items-center gap-3 flex-wrap">
              <span>started {relativeTime(meta.started_at)}</span>
              <span>{dur}</span>
              {meta.pid && <span className="font-mono">pid {meta.pid}</span>}
              {meta.internal_messages_count > 0 && <span>{meta.internal_messages_count} internal msgs</span>}
              {meta.total_size !== undefined && <span>{formatBytes(meta.total_size)}</span>}
              {meta.workdir && <span className="font-mono [overflow-wrap:anywhere]">{meta.workdir.replace(/^\/home\/[^/]+\//, "~/")}</span>}
            </div>
          </div>
          {(meta.status === "failed" || meta.status === "killed") && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => retry.mutate()}
              loading={retry.isPending}
              title="Re-run this spawn — mints a fresh GitHub token and checkout where the original had one"
            >
              <RotateCw className="size-3.5" />
              Retry
            </Button>
          )}
          {isRunning && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => { if (confirm(`Kill ${meta.persona} spawn${meta.pid ? ` (pid ${meta.pid})` : ""}?`)) kill.mutate(); }}
              loading={kill.isPending}
              title={meta.pid ? "Send SIGTERM to the spawn process" : "No pid recorded — clears the stale record if the process is gone"}
            >
              <Ban className="size-3.5" />
              {meta.pid ? "Kill" : "Clear"}
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {trigger && <TriggerCard t={trigger} />}
        <Section label="Prompt (raw spawn input)" content={prompt || "(empty)"} mono />
        {stdout && <Section label={`stdout · ${stdout.length.toLocaleString()} chars`} content={stdout} mono />}
        {stderr && <Section label={`stderr · ${stderr.length.toLocaleString()} chars`} content={stderr} mono tone="danger" />}
        {!stdout && !stderr && (
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <Terminal className="size-4" />
            <span>No output captured yet.</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ label, content, mono, tone }: { label: string; content: string; mono?: boolean; tone?: "danger" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1.5">{label}</div>
      <pre
        className={cn(
          "panel px-3 py-2 text-xs whitespace-pre-wrap [overflow-wrap:anywhere] max-h-[50vh] overflow-y-auto",
          mono ? "font-mono" : "font-sans",
          tone === "danger" ? "text-danger" : "text-fg",
        )}
      >
        {content}
      </pre>
    </div>
  );
}

// The envelope, parsed — "why this spawn ran and what it was answering."
function TriggerCard({ t }: { t: import("../lib/types").SpawnTrigger }) {
  const chat = t.chat_name || t.chat_id;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1.5">Trigger</div>
      <div className="panel px-3 py-2.5 space-y-2">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          {t.platform && <Pill tone="info">{t.platform}</Pill>}
          {t.kind && <Pill tone="neutral">{t.kind}</Pill>}
          {t.observe && <Pill tone="warning">observer</Pill>}
          {t.channel && <span className="text-fg-subtle">channel <span className="font-mono text-fg">{t.channel}</span></span>}
          {t.route && <span className="text-fg-subtle">route <span className="font-mono text-fg">{t.route}</span></span>}
        </div>
        {(chat || t.sender) && (
          <div className="text-xs text-fg-muted [overflow-wrap:anywhere]">
            {chat && <span className="text-fg">{chat}</span>}
            {t.chat_name && t.chat_id && <span className="font-mono text-[10px] text-fg-subtle"> {t.chat_id}</span>}
            {t.chat_kind && <span className="text-fg-subtle"> ({t.chat_kind})</span>}
            {t.sender && <span> · from <span className="text-fg">{t.sender}</span></span>}
          </div>
        )}
        {(t.participants?.length ?? 0) > 0 && (
          <div className="flex items-center gap-1 flex-wrap text-[11px] text-fg-muted">
            <span className="text-fg-subtle">people:</span>
            {t.participants!.map((p) => (
              <span key={p.id} className="px-1.5 rounded bg-surface-2 text-fg">{p.name}</span>
            ))}
          </div>
        )}
        {t.subject && <div className="text-xs text-fg-subtle [overflow-wrap:anywhere]">{t.subject}</div>}
        {t.messages.length > 0 ? (
          <div className="space-y-1 border-l-2 border-border pl-2">
            {t.messages.map((m, i) => (
              <div key={i} className="text-sm text-fg whitespace-pre-wrap [overflow-wrap:anywhere]">
                {m.text || (m.attachments.length ? "" : "—")}
                {m.attachments.length > 0 && <span className="text-[10px] text-fg-subtle ml-1">[{m.attachments.join(", ")}]</span>}
              </div>
            ))}
          </div>
        ) : t.body ? (
          <div className="text-sm text-fg whitespace-pre-wrap [overflow-wrap:anywhere] border-l-2 border-border pl-2">{t.body}</div>
        ) : null}
        {t.thread_history_count > 0 && (
          <div className="text-[10px] text-fg-subtle">+ {t.thread_history_count} earlier message{t.thread_history_count !== 1 ? "s" : ""} of context</div>
        )}
      </div>
    </div>
  );
}
